import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { usePIM } from '../../context/PIMContext';

export const Pallet3DVisualizer = ({ arrangement }) => {
    const containerRef = useRef(null);
    const sceneRef = useRef(null);
    const cameraRef = useRef(null);
    const rendererRef = useRef(null);
    const frameRef = useRef(null);
    const mainObjectRef = useRef(null);
    const { packagingGroups } = usePIM();

    useEffect(() => {
        if (!containerRef.current) return;

        // Setup
        const width = containerRef.current.clientWidth;
        const height = containerRef.current.clientHeight;

        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0x1c1917); // Dark gray
        sceneRef.current = scene;

        const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 2000);
        camera.position.set(350, 350, 600); // Zoomed out
        camera.lookAt(0, -50, 0);
        cameraRef.current = camera;

        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setSize(width, height);
        renderer.shadowMap.enabled = true;

        // Clear any existing canvas to prevent duplicates
        while (containerRef.current.firstChild) {
            containerRef.current.removeChild(containerRef.current.firstChild);
        }
        containerRef.current.appendChild(renderer.domElement);
        rendererRef.current = renderer;

        // Lights
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
        scene.add(ambientLight);

        const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
        directionalLight.position.set(200, 500, 200);
        directionalLight.castShadow = true;
        scene.add(directionalLight);

        // Animation Loop
        const animate = () => {
            frameRef.current = requestAnimationFrame(animate);
            if (mainObjectRef.current) {
                mainObjectRef.current.rotation.y += 0.005;
            }
            renderer.render(scene, camera);
        };
        animate();

        // Cleanup
        const container = containerRef.current;
        return () => {
            if (frameRef.current) cancelAnimationFrame(frameRef.current);
            if (rendererRef.current && container) {
                container.removeChild(rendererRef.current.domElement);
                rendererRef.current.dispose();
            }
        };
    }, []);

    // Handle Resize
    useEffect(() => {
        const handleResize = () => {
            if (!containerRef.current || !cameraRef.current || !rendererRef.current) return;
            const width = containerRef.current.clientWidth;
            const height = containerRef.current.clientHeight;
            cameraRef.current.aspect = width / height;
            cameraRef.current.updateProjectionMatrix();
            rendererRef.current.setSize(width, height);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Update Scene when arrangement changes
    useEffect(() => {
        if (!sceneRef.current || !arrangement) return;

        // Clear previous objects
        if (mainObjectRef.current) {
            sceneRef.current.remove(mainObjectRef.current);
            mainObjectRef.current = null;
        }

        const parent = new THREE.Group();
        const scale = 0.24; // Scale factor

        // Extract dynamic dimensions from arrangement or use defaults
        const pWidth = arrangement.palletWidth || 800;
        const pLength = arrangement.palletLength || 1200;

        // Determine pallet visual height: 144mm if included, otherwise a thin base (e.g., 20mm)
        const pHeight = arrangement.includePalletHeight ? 144 : 20;

        // Create Pallet Base
        const palletGeometry = new THREE.BoxGeometry(
            pWidth * scale,
            pHeight * scale,
            pLength * scale
        );
        const palletMaterial = new THREE.MeshStandardMaterial({
            color: 0xd2b48c,
            roughness: 0.8,
            metalness: 0.2
        });
        const pallet = new THREE.Mesh(palletGeometry, palletMaterial);
        pallet.receiveShadow = true;
        // Position: Top of pallet is at y=0, so move down by half height
        pallet.position.y = -(pHeight * scale) / 2;
        parent.add(pallet);

        // Determine Box Color
        let boxColor = 0x1e3a8a; // Default Blue-900
        if (arrangement.color) {
            boxColor = arrangement.color;
        } else {
            const masterCartonGroup = packagingGroups.find(g => g.name && g.name.trim().toLowerCase() === 'master carton');
            if (masterCartonGroup && masterCartonGroup.color) {
                boxColor = masterCartonGroup.color;
            }
        }

        const boxMaterial = new THREE.MeshStandardMaterial({
            color: new THREE.Color(boxColor),
            roughness: 0.3,
            metalness: 0.1,
            transparent: true,
            opacity: 0.6
        });

        // Create Boxes
        // Logic remains largely the same, but positioning relies on pWidth/pLength for centering
        if (arrangement.totalBoxes > 5000) {
            // Simplified block for huge numbers
            const totalLoadHeight = arrangement.totalHeight - (arrangement.includePalletHeight ? 144 : 0);
            const volumeGeometry = new THREE.BoxGeometry(
                pWidth * scale,
                totalLoadHeight * scale,
                pLength * scale
            );
            const volume = new THREE.Mesh(volumeGeometry, boxMaterial);
            volume.position.y = (totalLoadHeight * scale) / 2;

            const edges = new THREE.EdgesGeometry(volumeGeometry);
            const line = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0x1e3a8a }));
            volume.add(line);
            parent.add(volume);
        } else {
            // Reusable Materials & Geometries
            const lineMaterial = new THREE.LineBasicMaterial({ color: 0x000000 });

            if (arrangement.isMixedOrientation && arrangement.layerLayout) {
                const boxGeoStandard = new THREE.BoxGeometry(
                    arrangement.boxWidth * scale,
                    arrangement.boxHeight * scale,
                    arrangement.boxLength * scale
                );
                const edgesStandard = new THREE.EdgesGeometry(boxGeoStandard);

                const boxGeoRotated = new THREE.BoxGeometry(
                    arrangement.boxLength * scale,
                    arrangement.boxHeight * scale,
                    arrangement.boxWidth * scale
                );
                const edgesRotated = new THREE.EdgesGeometry(boxGeoRotated);

                for (let l = 0; l < arrangement.layers; l++) {
                    const y = l * arrangement.boxHeight * scale;
                    arrangement.layerLayout.forEach(box => {
                        const isRotated = box.isAlternateOrientation;
                        const geometry = isRotated ? boxGeoRotated : boxGeoStandard;
                        const edges = isRotated ? edgesRotated : edgesStandard;

                        const mesh = new THREE.Mesh(geometry, boxMaterial);
                        mesh.castShadow = true;
                        mesh.receiveShadow = true;

                        // Calculate position relative to pallet center
                        // box.x is from 0,0 corner. Center is at pWidth/2, pLength/2
                        const xPos = (box.x * scale) - (pWidth * scale) / 2 + (box.width * scale) / 2;
                        const zPos = (box.y * scale) - (pLength * scale) / 2 + (box.length * scale) / 2;

                        mesh.position.set(xPos, y + (arrangement.boxHeight * scale) / 2, zPos);

                        const line = new THREE.LineSegments(edges, lineMaterial);
                        mesh.add(line);

                        parent.add(mesh);
                    });
                }
            } else {
                // Standard Grid Layout
                const boxGeometry = new THREE.BoxGeometry(
                    arrangement.boxWidth * scale,
                    arrangement.boxHeight * scale,
                    arrangement.boxLength * scale
                );
                const boxEdges = new THREE.EdgesGeometry(boxGeometry);

                for (let l = 0; l < arrangement.layers; l++) {
                    const y = l * arrangement.boxHeight * scale;
                    for (let i = 0; i < arrangement.boxesInWidth; i++) {
                        for (let j = 0; j < arrangement.boxesInLength; j++) {
                            const x = i * arrangement.boxWidth * scale;
                            const z = j * arrangement.boxLength * scale;

                            const box = new THREE.Mesh(boxGeometry, boxMaterial);
                            box.castShadow = true;
                            box.receiveShadow = true;

                            const xPos = x - (pWidth * scale) / 2 + (arrangement.boxWidth * scale) / 2;
                            const zPos = z - (pLength * scale) / 2 + (arrangement.boxLength * scale) / 2;

                            box.position.set(
                                xPos,
                                y + (arrangement.boxHeight * scale) / 2,
                                zPos
                            );

                            const line = new THREE.LineSegments(boxEdges, lineMaterial);
                            box.add(line);

                            parent.add(box);
                        }
                    }
                }
            }
        }

        // Center the object vertically
        // The boxes start at y=0 and go up. The pallet starts at y=0 and goes down.
        // Total visual height roughly = Load Height + Pallet Visual Height
        const loadHeight = arrangement.layers * arrangement.boxHeight * scale;
        // We want the center of the total mass to be at y=0? 
        // Or keep the pallet bottom at y = -pHeight?
        // Let's keep the logic simple: Move the whole group down so the vertical center is at 0
        // Top of load = loadHeight
        // Bottom of pallet = -pHeight * scale
        // Center = (loadHeight - pHeight * scale) / 2

        // Current pivot is at y=0 (interface between pallet and boxes)
        // To center it:
        const centerOffset = (loadHeight - (pHeight * scale)) / 2;
        parent.position.y = -centerOffset;

        sceneRef.current.add(parent);
        mainObjectRef.current = parent;

    }, [arrangement, packagingGroups]);

    return (
        <div className="h-full w-full relative bg-stone-900 rounded-lg overflow-hidden z-10">
            <div ref={containerRef} className="w-full h-full" />
            {!arrangement && (
                <div className="absolute inset-0 flex items-center justify-center text-stone-500">
                    No arrangement selected
                </div>
            )}
        </div>
    );
};
